import { getApiDomain } from "../config";
import { SignWithStreak } from "../models/Sign";
import SignProvider from "../models/SignProvider";
import SignVocab from "../models/SignVocab";
import Vocab from "../models/Vocab";

export interface ApiResponse<T> {
    data?: T;
    error?: string;
    status?: number;
    detail?: string;
}
  
export default async function apiRequest<T>(
        url: string,
        method: string = 'GET',
        body?: any,
        headers: Record<string, string> = {},
        options?: { signal?: AbortSignal }
    ): Promise<ApiResponse<T>> {
    try {
        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: options?.signal, // Pass the signal here
        });
        
        console.log("[Response]", response);

        const contentType = response.headers.get('Content-Type');
        const isJsonResponse = contentType && contentType.includes('application/json');
        const data = isJsonResponse ? await response.json() : null;

        if (!response.ok) {
            return {
                error: data?.message || response.statusText,
                detail: data?.detail ?? undefined,
                status: response.status,
            };
        }

        return { data, status: response.status };
    } catch (error: any) {
        if (error.name === 'AbortError') {
            console.warn('Fetch aborted:', url);
            return {  };
        }

        return {
            error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
    }
};

export async function deleteSignVocab(sign_vocab: SignVocab): Promise<{ok: boolean}> {
    const response = await apiRequest<{ok: boolean}>(`${getApiDomain()}/vocabs/`, 'DELETE', sign_vocab);
    
    if (response.error) {
        console.error('[deleteSignVocab]', response.error);
        return {ok: false};
    }

    return response.data ? {ok: true} : {ok: false};
}

export async function addSignVocab(sign_vocab: SignVocab): Promise<{ok: boolean}> {
    const response = await apiRequest<{ok: boolean}>(`${getApiDomain()}/vocabs/`, 'POST', sign_vocab);
    
    if (response.error) {
        console.error('[addSignVocab]', response.error);
        return {ok: false};
    }

    return response.data ? {ok: true} : {ok: false};
}

export async function addVocab(name: string, max_level: number): Promise<{ok: boolean}> {
    const response = await apiRequest<{ok: boolean}>(`${getApiDomain()}/vocabs/create/`, 'POST', {name: name, max_level: max_level});
    
    if (response.error) {
        console.error('[addVocab]', response.error);
        return {ok: false};
    }

    return response.data ? {ok: true} : {ok: false};
}

export async function learnSign(sign_id: number, learn: boolean): Promise<{learnt: boolean | null}> {
    const response: ApiResponse<{learnt: boolean | null}> = await apiRequest<{learnt: boolean | null}>(
        `${getApiDomain()}/practice/`, 'POST',
        {
            sign_id: sign_id,
            learn: learn
        }
    );
    
    if (response.error) {
        console.error('[learnSign]', response.error);
        return {learnt: null}
    }

    return response.data ?? {learnt: null};
}

export async function practiceSign(sign_id: number, result: number): Promise<SignWithStreak | null> {
    const response: ApiResponse<SignWithStreak> = await apiRequest<SignWithStreak>(
        `${getApiDomain()}/practice/`, 'PUT',
        {
            sign_id: sign_id,
            result: result
        }
    );
    
    if (response.error) {
        console.error('[practiceSign]', response.error);
        throw new Error(response.detail !== undefined ? `[${response.error}]: ${response.detail}` : response.error);
    }

    return response.data ?? null;
}

export async function vocabs(): Promise<Vocab[]> {
    const response: ApiResponse<Vocab[]> = await apiRequest<Vocab[]>(`${getApiDomain()}/vocabs/`, 'GET');
    
    if (response.error) {
        console.error('[vocabs]', response.error);
        throw new Error(response.error);
    }

    return response.data ?? [];
}

export async function providers(): Promise<SignProvider[]> {
    const response: ApiResponse<SignProvider[]> = await apiRequest<SignProvider[]>(`${getApiDomain()}/providers/`, 'GET');
    
    if (response.error) {
        console.error('[providers]', response.error);
        throw new Error(response.error);
    }

    return response.data ?? [];
}
